import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KeyRound, ShieldCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [conf, setConf] = useState("");
  const [saving, setSaving] = useState(false);
  const [recovery, setRecovery] = useState("");
  const [copied, setCopied] = useState(false);

  const reset = () => { setCur(""); setNext(""); setConf(""); setRecovery(""); setCopied(false); };

  const submit = async () => {
    if (next.length < 6) { toast.error("Yeni şifre en az 6 karakter olmalı"); return; }
    if (next !== conf) { toast.error("Yeni şifreler eşleşmiyor"); return; }
    setSaving(true);
    try {
      await api.changePassword(cur, next);
      toast.success("Şifreniz güncellendi");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setSaving(false);
    }
  };

  const genCode = async () => {
    try {
      const d = await api.generateRecoveryCode();
      setRecovery(d.recovery_code);
      toast.success("Kurtarma kodu oluşturuldu — güvenli bir yere kaydedin");
    } catch {
      toast.error("Kurtarma kodu oluşturulamadı");
    }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(recovery);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-card">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><KeyRound className="h-5 w-5 text-primary" /> Şifre Değiştir</DialogTitle>
          <DialogDescription>Mevcut şifrenizi girerek yeni bir şifre belirleyin.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div><Label>Mevcut Şifre</Label><Input data-testid="cp-current" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Yeni Şifre</Label><Input data-testid="cp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Yeni Şifre (Tekrar)</Label><Input data-testid="cp-confirm" type="password" value={conf} onChange={(e) => setConf(e.target.value)} className="mt-1.5" /></div>
          <Button data-testid="cp-submit" onClick={submit} disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/90">{saving ? "Kaydediliyor..." : "Şifreyi Güncelle"}</Button>

          <div className="rounded-lg border border-border bg-secondary/30 p-3.5">
            <div className="mb-1.5 flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Kurtarma Kodu</div>
            <p className="mb-2.5 text-xs text-muted-foreground">Şifrenizi unutursanız e-posta olmadan sıfırlamak için tek kullanımlık bir kod oluşturun ve güvenli bir yere kaydedin.</p>
            {recovery ? (
              <div className="flex items-center gap-2">
                <code data-testid="cp-recovery-code" className="flex-1 rounded-md bg-background px-3 py-2 font-mono text-sm tracking-wider text-emerald-300">{recovery}</code>
                <button onClick={copyCode} className="flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-secondary">{copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}</button>
              </div>
            ) : (
              <Button data-testid="cp-gen-recovery" variant="outline" size="sm" onClick={genCode}>Kurtarma Kodu Oluştur</Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
